import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { ChevronDown, X } from "lucide-react";

const pluralizeLabel = (label) => {
  const value = label.toLowerCase();
  if (/(s|x|z|ch|sh)$/.test(value)) return `${value}es`;
  if (/[^aeiou]y$/.test(value)) return `${value.slice(0, -1)}ies`;
  return `${value}s`;
};

/**
 * Consistent multi-select filter used across all data pages.
 * options: array of strings or {value, label} objects
 * selected: array of selected values ([] = all)
 */
export default function MultiSelectFilter({
  label, options = [], selected = [], onChange, testId, className = "", searchable = true,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const matchingOpts = normalizedSearch
    ? opts.filter((option) => String(option.label || "").toLowerCase().includes(normalizedSearch))
    : [];
  const showSelectAll = normalizedSearch
    && matchingOpts.length > 1
    && matchingOpts.length < opts.length;

  const toggle = (value) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };
  const selectAll = () => {
    const values = new Set(selected);
    matchingOpts.forEach((option) => values.add(option.value));
    onChange([...values]);
  };

  const selectedLabels = opts.filter((o) => selected.includes(o.value)).map((o) => o.label);
  const display = selected.length === 0
    ? `All ${pluralizeLabel(label)}`
    : selected.length === 1
      ? selectedLabels[0]
      : `${selectedLabels[0]} +${selected.length - 1}`;

  return (
    <Popover open={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      if (!nextOpen) setSearchQuery("");
    }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testId}
          className={`justify-between rounded-lg font-normal h-9 ${selected.length ? "border-teal-300 bg-teal-50/50 dark:bg-teal-950/30 dark:border-teal-800" : ""} ${className}`}>
          <span className="truncate text-sm">{display}</span>
          <span className="flex items-center gap-1 ml-1.5 shrink-0">
            {selected.length > 1 && (
              <Badge className="h-4 px-1.5 text-[10px] rounded-full bg-teal-600 text-white hover:bg-teal-600"
                data-testid={`${testId}-count`}>{selected.length}</Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          {searchable && opts.length > 6 && (
            <CommandInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={`Search ${label.toLowerCase()}...`}
              data-testid={`${testId}-search`}
            />
          )}
          <CommandList className="max-h-64">
            <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
            <CommandGroup>
              {showSelectAll && (
                <CommandItem
                  value={searchQuery}
                  onSelect={selectAll}
                  className="cursor-pointer gap-2"
                  data-testid={`${testId}-select-all`}>
                  <Checkbox
                    checked={matchingOpts.every((option) => selected.includes(option.value))}
                    className="pointer-events-none"
                  />
                  <span className="truncate">Select All</span>
                </CommandItem>
              )}
              {opts.map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => toggle(o.value)}
                  className="cursor-pointer gap-2" data-testid={`${testId}-option-${o.value}`}>
                  <Checkbox checked={selected.includes(o.value)} className="pointer-events-none" />
                  <span className="truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 && (
            <div className="border-t p-1.5">
              <Button variant="ghost" size="sm" className="w-full h-7 text-xs justify-center text-muted-foreground"
                data-testid={`${testId}-clear`} onClick={() => { onChange([]); setOpen(false); }}>
                <X className="h-3 w-3 mr-1" /> Clear selection
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
